import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { PaginateList } from 'src/app/common/types';
import { getDefaultDisplayDate } from 'src/app/common/utils/time-utils';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import ItemDeleteDialog from 'src/app/common/components/ItemDeleteDialog';
import { ContractSettingItem } from '../../../../types/contract-setting-types';
import { deleteContractSettingItem } from '../../../../network/contract-setting-crud';
import { contractSettingPath } from '../../contract-setting-routes';
import { ContractSettingSortState } from '../contract-setting-listing-page.hook';
import { isPermitted, useJWT } from 'src/app/common/utils';
import { map } from 'lodash';
import { AdminCommissionToolPermissionType } from 'src/app/modules/CommissionTool/constants';
import { useTranslation } from 'src/app/common/hook/translation.hook';

type ContractSettingListProps = {
  isLoading: boolean;
  contractSettingList?: PaginateList<ContractSettingItem>;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onSort: (newSortState: ContractSettingSortState) => void;
};

type DialogState = {
  open: boolean;
  id: string;
  name: string;
};

const useStyles = makeStyles()(() => ({
  tableWrapper: {
    '& .table-body > .MuiPaper-root': {
      height: 'auto',
    },
    '& .table-body .MuiTable-root': {
      tableLayout: 'auto',
      width: 'max-content',
    },
    '& .table-body .MuiTableCell-root': {
      whiteSpace: 'nowrap',
      width: 'max-content',
      wordBreak: 'keep-all',
    },
  },
}));

export const ContractSettingList: FC<ContractSettingListProps> = ({
  isLoading,
  contractSettingList,
  onRefresh,
  onChangePage,
  onSort,
}) => {
  const { classes } = useStyles();
  const Translation = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  let updatePermission = false;
  let createPermission = false;
  let deletedPermission = false;
  const jwt = useJWT() || {};

  if (jwt.permissions) {
    const jwtPermission = map(jwt.permissions, (_, key) => key);
    updatePermission = isPermitted([AdminCommissionToolPermissionType.CONTRACT_SETTING_UPDATE], jwtPermission);
    createPermission = isPermitted([AdminCommissionToolPermissionType.CONTRACT_SETTING_CREATE], jwtPermission);
    deletedPermission = isPermitted([AdminCommissionToolPermissionType.CONTRACT_SETTING_DELETE], jwtPermission);
  }

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    id: '',
    name: '',
  });

  const onDelete = async (id: string) => {
    try {
      await deleteContractSettingItem(id, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: Translation('master.data.setting.update.successNotName'),
          },
        ]),
      );
    } catch (err) {}
  };

  return (
    <>
      <ItemDeleteDialog
        key={`delete-contract-setting-dialog-${dialogState.open}`}
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, id: '', name: '' })}
        onRefresh={onRefresh}
        onDelete={() => onDelete(dialogState.id)}
        content={Translation('master.data.setting.delete.confirm')}
      />
      <div className={classes.tableWrapper}>
        <PruTable
          title={Translation('contract.setting.list')}
          disableBulkSelect
          operationSticky
          headerBtnDef={
            createPermission
              ? [
                  {
                    color: 'primary',
                    title: Translation('contract.setting.button.import'),
                    onClick: () => history.push(`${contractSettingPath}/import`),
                    condition: () => createPermission,
                  },
                  {
                    color: 'secondary',
                    title: Translation('commission-tool.button.create'),
                    onClick: () => history.push(`${contractSettingPath}/create`),
                    condition: () => createPermission,
                  },
                ]
              : []
          }
          operationDef={
            updatePermission || deletedPermission
              ? [
                  {
                    title: Translation('section.common.operation.edit'),
                    tooltipText: 'Edit',
                    onClick: (row) => history.push(`${contractSettingPath}/edit/${row._id}`),
                    condition: () => updatePermission,
                  },
                  {
                    title: Translation('section.common.operation.delete'),
                    tooltipText: 'Delete',
                    onClick: (row) =>
                      setDialogState({
                        open: true,
                        id: row._id,
                        name: row.productName?.value || row._id,
                      }),
                    condition: () => deletedPermission,
                  },
                ]
              : []
          }
          columnDef={[
            {
              isId: true,
              hidden: true,
              keyIndex: '_id',
              displayName: '',
              renderData: () => '',
            },
            {
              keyIndex: 'productName',
              displayName: Translation('master.data.setting.name.PRODUCT_NAME'),
              renderData: (row) => row.productName?.value || '',
            },
            {
              keyIndex: 'insurancePlan',
              displayName: Translation('master.data.setting.name.INSURANCE_PLAN'),
              renderData: (row) => row.insurancePlan?.value || '',
            },
            {
              keyIndex: 'insurancePremium',
              displayName: Translation('master.data.setting.name.INSURANCE_PREMIUM'),
              renderData: (row) => row.insurancePremium?.value || '',
            },
            {
              keyIndex: 'premiumPaymentTerm',
              displayName: Translation('master.data.setting.name.PAYMENT_TERM'),
              renderData: (row) => row.premiumPaymentTerm?.value || '',
            },
            {
              keyIndex: 'premiumPaymentFrequency',
              displayName: Translation('master.data.setting.name.PAYMENT_FREQUENCY'),
              renderData: (row) => row.premiumPaymentFrequency?.value || '',
            },
            {
              keyIndex: 'premiumPaymentYear',
              displayName: Translation('master.data.setting.name.PAYMENT_YEAR'),
              renderData: (row) => row.premiumPaymentYear?.value || '',
            },
            {
              keyIndex: 'effectiveDate',
              displayName: Translation('master.data.setting.name.EFFECTIVE_DATE'),
              renderData: (row) => row.effectiveDate?.value || '',
            },
            {
              keyIndex: 'riderCount',
              displayName: Translation('master.data.setting.name.RIDER_COUNT'),
              renderData: (row) => row.riderCount?.value || '',
            },
            {
              keyIndex: 'commissionRate',
              displayName: Translation('contract.setting.commissionRate') + '%',
              renderData: (row) => (row.commissionRate ?? 0).toString(),
            },
            {
              keyIndex: 'createdAt',
              align: 'center',
              displayName: Translation('master.data.setting.createdAt'),
              renderData: (row) => getDefaultDisplayDate(row.createdAt, 'dateTime'),
            },
            {
              keyIndex: 'createdBy',
              align: 'center',
              displayName: Translation('master.data.setting.createdBy'),
              renderData: (row) => row.createdBy || '',
            },
            {
              keyIndex: 'updatedAt',
              align: 'center',
              displayName: Translation('master.data.setting.updatedAt'),
              renderData: (row) => getDefaultDisplayDate(row.updatedAt, 'dateTime'),
            },
            {
              keyIndex: 'updatedBy',
              align: 'center',
              displayName: Translation('master.data.setting.updatedBy'),
              renderData: (row) => row.updatedBy || '',
            },
          ]}
          isLoading={isLoading}
          onRefresh={onRefresh}
          dataSource={contractSettingList?.items}
          totalPages={
            contractSettingList?.total && contractSettingList?.limit
              ? Math.ceil(contractSettingList.total / contractSettingList.limit)
              : undefined
          }
          totalRecords={contractSettingList?.total}
          onChangePage={onChangePage}
        />
      </div>
    </>
  );
};
